import axios from 'axios';
import {
  ShiftType,
  CreateShiftTypeRequest,
  UpdateShiftTypeRequest,
  AssignShiftTypeRequest,
  RemoveShiftTypeRequest,
} from '@/types/shiftType';
import { User } from '@/types/user';
import { apiCache } from './apiCache';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const shiftTypeService = {
  async getAllShiftTypes(includeInactive: boolean = false): Promise<ShiftType[]> {
    const cacheKey = includeInactive ? '/shift-types?includeInactive=true' : '/shift-types';
    return apiCache.getCachedData(
      cacheKey,
      async () => {
        const response = await apiClient.get('/shift-types', {
          params: includeInactive ? { includeInactive: true } : {}
        });
        return response.data.shiftTypes;
      },
      undefined,
      { cooldown: 10000 } // 10 second cooldown for shift type data
    );
  },

  async setActive(id: number, isActive: boolean): Promise<ShiftType> {
    const response = await apiClient.put(`/shift-types/${id}`, { is_active: isActive });
    apiCache.clearCache('/shift-types');
    apiCache.clearCache('/shift-types?includeInactive=true');
    return response.data.shiftType;
  },

  async getShiftTypeById(id: number): Promise<ShiftType> {
    const response = await apiClient.get(`/shift-types/${id}`);
    return response.data.shiftType;
  },

  async createShiftType(data: CreateShiftTypeRequest): Promise<ShiftType> {
    const response = await apiClient.post('/shift-types', data);
    apiCache.clearCache('/shift-types');
    apiCache.clearCache('/shift-types?includeInactive=true');
    return response.data.shiftType;
  },

  async updateShiftType(id: number, data: UpdateShiftTypeRequest): Promise<ShiftType> {
    const response = await apiClient.put(`/shift-types/${id}`, data);
    apiCache.clearCache('/shift-types');
    apiCache.clearCache('/shift-types?includeInactive=true');
    return response.data.shiftType;
  },

  async deleteShiftType(id: number): Promise<void> {
    await apiClient.delete(`/shift-types/${id}`);
    apiCache.clearCache('/shift-types');
    apiCache.clearCache('/shift-types?includeInactive=true');
  },

  async assignShiftTypeToEmployees(shiftTypeId: number, employeeIds: number[]): Promise<void> {
    await Promise.all(
      employeeIds.map((employeeId) =>
        apiClient.post('/shift-types/assign', { employeeId, shiftTypeId })
      )
    );
    apiCache.clearCache('/shift-types');
    apiCache.clearCache('/shift-types?includeInactive=true');
  },

  async removeShiftTypeFromEmployees(shiftTypeId: number, employeeIds: number[]): Promise<void> {
    await Promise.all(
      employeeIds.map((employeeId) =>
        apiClient.post('/shift-types/remove', { employeeId, shiftTypeId })
      )
    );
    apiCache.clearCache('/shift-types');
    apiCache.clearCache('/shift-types?includeInactive=true');
  },

  async getEmployeesForShiftType(shiftTypeId: number): Promise<User[]> {
    try {
      const response = await apiClient.get(`/shift-types/${shiftTypeId}/employees`);
      return response.data.employees;
    } catch (error: any) {
      console.error('Error fetching employees for shift type:', error);
      if (error.response?.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      throw error;
    }
  },

  async assignShiftTypeToEmployee(data: AssignShiftTypeRequest): Promise<void> {
    await apiClient.post('/shift-types/assign', data);
  },

  async removeShiftTypeFromEmployee(data: RemoveShiftTypeRequest): Promise<void> {
    await apiClient.post('/shift-types/remove', data);
  },

  async getEmployeeShiftTypes(employeeId: number): Promise<ShiftType[]> {
    const response = await apiClient.get(`/shift-types/employee/${employeeId}`);
    return response.data.shiftTypes;
  },
}; 